import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
// --- Obstacle types ---
interface Circle {
  cx: number;
  cy: number;
  r: number;
}
interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
  angle?: number;
}
export function PuttingHero() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isRevealed, setIsRevealed] = useState(false);
  const [showSinkFlash, setShowSinkFlash] = useState(false);
  const isRevealedRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasInteractedRef = useRef(false);
  const triggerReveal = useCallback(() => {
    if (isRevealedRef.current) return;
    isRevealedRef.current = true;
    setIsRevealed(true);
  }, []);
  const resetTimeout = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (!isRevealedRef.current) {
      timeoutRef.current = setTimeout(() => triggerReveal(), hasInteractedRef.current ? 18000 : 10000);
    }
  }, [triggerReveal]);
  useEffect(() => {
    resetTimeout();
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [resetTimeout]);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || isRevealed) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let raf: number;
    let W = window.innerWidth;
    let H = window.innerHeight;
    let t = 0;
    // Course dimensions (centered in viewport)
    let courseW = 0,
      courseH = 0,
      courseX = 0,
      courseY = 0;
    let scale = 1;
    // Logical course size (design space)
    const LW = 420,
      LH = 700;
    const computeLayout = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = W * 2;
      canvas.height = H * 2;
      canvas.style.width = W + 'px';
      canvas.style.height = H + 'px';
      ctx.setTransform(2, 0, 0, 2, 0, 0);
      // Fit course in viewport with padding
      const padX = W < 600 ? 30 : 80;
      const padTop = 90;
      const padBot = 80;
      const availW = Math.max(W - padX * 2, 1);
      const availH = Math.max(H - padTop - padBot, 1);
      scale = Math.max(Math.min(availW / LW, availH / LH), 0.1);
      courseW = LW * scale;
      courseH = LH * scale;
      courseX = (W - courseW) / 2;
      courseY = padTop + (availH - courseH) / 2;
    };
    // Transform logical → screen
    const tx = (lx: number) => courseX + lx * scale;
    const ty = (ly: number) => courseY + ly * scale;
    const ts = (ls: number) => ls * scale;
    // Ball
    const ball = {
      x: LW / 2,
      y: LH - 60,
      vx: 0,
      vy: 0,
      r: 7
    };
    const HOLE = {
      x: LW / 2,
      y: 55,
      r: 11
    };
    const FLAG_HEIGHT = 55;
    // State
    let dragging = false;
    let dragStart = {
      x: 0,
      y: 0
    };
    let dragCurrent = {
      x: 0,
      y: 0
    };
    let ballMoving = false;
    let isSunk = false;
    let puttCount = 0;
    const MAX_PUTTS = 6;
    // Trail
    const trail: {
      x: number;
      y: number;
      age: number;
    }[] = [];
    // Particles
    const particles: {
      x: number;
      y: number;
      vx: number;
      vy: number;
      life: number;
      size: number;
      color: string;
    }[] = [];
    // --- Obstacles ---
    // Bumper walls (rectangles the ball bounces off)
    const bumpers: Rect[] = [{
      x: 60,
      y: 240,
      w: 100,
      h: 12,
      angle: 0.15
    }, {
      x: 260,
      y: 320,
      w: 100,
      h: 12,
      angle: -0.15
    }, {
      x: 140,
      y: 440,
      w: 80,
      h: 12,
      angle: 0
    }];
    // Rocks (circles the ball bounces off)
    const rocks: Circle[] = [{
      cx: 120,
      cy: 180,
      r: 18
    }, {
      cx: 310,
      cy: 200,
      r: 15
    }, {
      cx: 80,
      cy: 400,
      r: 14
    }, {
      cx: 340,
      cy: 480,
      r: 16
    }, {
      cx: 200,
      cy: 150,
      r: 12
    }];
    // Sand traps (slow the ball)
    const sandTraps: Circle[] = [{
      cx: 320,
      cy: 140,
      r: 35
    }, {
      cx: 100,
      cy: 520,
      r: 30
    }, {
      cx: 280,
      cy: 400,
      r: 28
    }];
    // Water hazard (resets ball)
    const water: Rect = {
      x: 150,
      y: 270,
      w: 120,
      h: 50
    };
    // Windmill (rotating blades)
    const windmill = {
      cx: LW / 2,
      cy: 380,
      armLen: 55,
      armWidth: 10,
      speed: 1.2
    };
    // Course walls
    const wallBounce = (bx: number, by: number, bvx: number, bvy: number, br: number) => {
      let nx = bx,
        ny = by,
        nvx = bvx,
        nvy = bvy;
      // Left wall
      if (nx - br < 0) {
        nx = br;
        nvx = Math.abs(nvx) * 0.7;
      }
      // Right wall
      if (nx + br > LW) {
        nx = LW - br;
        nvx = -Math.abs(nvx) * 0.7;
      }
      // Top wall
      if (ny - br < 0) {
        ny = br;
        nvy = Math.abs(nvy) * 0.7;
      }
      // Bottom wall
      if (ny + br > LH) {
        ny = LH - br;
        nvy = -Math.abs(nvy) * 0.7;
      }
      return {
        x: nx,
        y: ny,
        vx: nvx,
        vy: nvy
      };
    };
    computeLayout();
    window.addEventListener('resize', () => computeLayout());
    // --- Input ---
    const toLogical = (sx: number, sy: number) => ({
      x: (sx - courseX) / scale,
      y: (sy - courseY) / scale
    });
    const onDown = (e: PointerEvent) => {
      if (isSunk || isRevealedRef.current || ballMoving) return;
      hasInteractedRef.current = true;
      resetTimeout();
      const rect = canvas.getBoundingClientRect();
      const p = toLogical(e.clientX - rect.left, e.clientY - rect.top);
      const dist = Math.hypot(p.x - ball.x, p.y - ball.y);
      if (dist < 35) {
        dragging = true;
        dragStart = {
          ...p
        };
        dragCurrent = {
          ...p
        };
        canvas.setPointerCapture(e.pointerId);
      }
    };
    const onMove = (e: PointerEvent) => {
      if (!dragging) return;
      resetTimeout();
      const rect = canvas.getBoundingClientRect();
      dragCurrent = toLogical(e.clientX - rect.left, e.clientY - rect.top);
    };
    const onUp = (e: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      canvas.releasePointerCapture(e.pointerId);
      resetTimeout();
      const dx = dragStart.x - dragCurrent.x;
      const dy = dragStart.y - dragCurrent.y;
      const dist = Math.hypot(dx, dy);
      if (dist > 8) {
        const power = Math.min(dist / 120, 1);
        const maxSpeed = 14;
        const speed = power * maxSpeed;
        const angle = Math.atan2(dy, dx);
        ball.vx = Math.cos(angle) * speed;
        ball.vy = Math.sin(angle) * speed;
        ballMoving = true;
        trail.length = 0;
        puttCount++;
        if (puttCount >= MAX_PUTTS) {
          setTimeout(() => {
            if (!isSunk) triggerReveal();
          }, 4000);
        }
      }
    };
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp);
    // --- Drawing ---
    const drawBackground = () => {
      // Dark surround
      ctx.fillStyle = '#081a0e';
      ctx.fillRect(0, 0, W, H);
      // Ambient glow behind course
      const glow = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, Math.max(W, H) * 0.5);
      glow.addColorStop(0, 'rgba(26,74,46,0.15)');
      glow.addColorStop(1, 'transparent');
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, W, H);
    };
    const drawCourse = () => {
      const x = courseX,
        y = courseY,
        w = courseW,
        h = courseH;
      const r = ts(15);
      // Outer shadow (ground plane)
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.6)';
      ctx.shadowBlur = 40;
      ctx.shadowOffsetY = 12;
      ctx.beginPath();
      ctx.roundRect(x - 4, y - 4, w + 8, h + 8, r + 4);
      ctx.fillStyle = '#071a0d';
      ctx.fill();
      ctx.restore();
      // Rough / collar (darker outer ring)
      ctx.beginPath();
      ctx.roundRect(x - 10, y - 10, w + 20, h + 20, r + 10);
      ctx.fillStyle = '#0a1f12';
      ctx.fill();
      // Fringe (slightly lighter than rough, darker than green)
      const fringeGrad = ctx.createLinearGradient(x, y, x, y + h);
      fringeGrad.addColorStop(0, '#133d22');
      fringeGrad.addColorStop(0.5, '#11361e');
      fringeGrad.addColorStop(1, '#0f3019');
      ctx.beginPath();
      ctx.roundRect(x - 5, y - 5, w + 10, h + 10, r + 5);
      ctx.fillStyle = fringeGrad;
      ctx.fill();
      // Fringe texture (tiny rough dots)
      ctx.save();
      ctx.beginPath();
      ctx.roundRect(x - 5, y - 5, w + 10, h + 10, r + 5);
      ctx.clip();
      for (let i = 0; i < 300; i++) {
        const fx = x - 5 + Math.random() * (w + 10);
        const fy = y - 5 + Math.random() * (h + 10);
        ctx.fillStyle = `rgba(${10 + Math.random() * 20},${40 + Math.random() * 25},${15 + Math.random() * 15},0.2)`;
        ctx.fillRect(fx, fy, 1 + Math.random(), 1 + Math.random());
      }
      ctx.restore();
      // Main putting green
      const greenGrad = ctx.createLinearGradient(x, y, x + w * 0.3, y + h);
      greenGrad.addColorStop(0, '#1f5a38');
      greenGrad.addColorStop(0.25, '#1c5233');
      greenGrad.addColorStop(0.5, '#1a4e30');
      greenGrad.addColorStop(0.75, '#18482c');
      greenGrad.addColorStop(1, '#164428');
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, r);
      ctx.fillStyle = greenGrad;
      ctx.fill();
      // Mow stripes (realistic alternating bands)
      ctx.save();
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, r);
      ctx.clip();
      const stripeW = ts(18);
      for (let sx = x; sx < x + w; sx += stripeW) {
        const stripeIndex = Math.floor((sx - x) / stripeW);
        if (stripeIndex % 2 === 0) {
          // Lighter stripe (mowed toward viewer)
          ctx.fillStyle = 'rgba(255,255,255,0.025)';
        } else {
          // Darker stripe (mowed away)
          ctx.fillStyle = 'rgba(0,0,0,0.025)';
        }
        ctx.fillRect(sx, y, stripeW, h);
      }
      // Subtle contour shading (undulation hints)
      const contour1 = ctx.createRadialGradient(x + w * 0.3, y + h * 0.35, 0, x + w * 0.3, y + h * 0.35, ts(120));
      contour1.addColorStop(0, 'rgba(255,255,255,0.02)');
      contour1.addColorStop(1, 'transparent');
      ctx.fillStyle = contour1;
      ctx.fillRect(x, y, w, h);
      const contour2 = ctx.createRadialGradient(x + w * 0.7, y + h * 0.65, 0, x + w * 0.7, y + h * 0.65, ts(100));
      contour2.addColorStop(0, 'rgba(0,0,0,0.03)');
      contour2.addColorStop(1, 'transparent');
      ctx.fillStyle = contour2;
      ctx.fillRect(x, y, w, h);
      // Grass grain (denser, more varied)
      for (let i = 0; i < 400; i++) {
        const gx = x + Math.random() * w;
        const gy = y + Math.random() * h;
        const brightness = Math.random();
        if (brightness > 0.5) {
          ctx.fillStyle = `rgba(${30 + Math.random() * 20},${80 + Math.random() * 40},${40 + Math.random() * 20},0.08)`;
        } else {
          ctx.fillStyle = `rgba(${15 + Math.random() * 10},${50 + Math.random() * 20},${25 + Math.random() * 10},0.1)`;
        }
        const sz = 0.8 + Math.random() * 1.2;
        ctx.fillRect(gx, gy, sz, sz);
      }
      // Subtle edge vignette inside the green
      const vignette = ctx.createRadialGradient(x + w / 2, y + h / 2, Math.min(w, h) * 0.3, x + w / 2, y + h / 2, Math.max(w, h) * 0.55);
      vignette.addColorStop(0, 'transparent');
      vignette.addColorStop(1, 'rgba(0,0,0,0.06)');
      ctx.fillStyle = vignette;
      ctx.fillRect(x, y, w, h);
      ctx.restore();
      // Edge highlight (top-left light source)
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, r);
      ctx.strokeStyle = 'rgba(255,255,255,0.04)';
      ctx.lineWidth = 1;
      ctx.stroke();
    };
    const drawSandTraps = () => {
      for (const s of sandTraps) {
        const sx = tx(s.cx),
          sy = ty(s.cy),
          sr = ts(s.r);
        // Lip
        ctx.beginPath();
        ctx.arc(sx, sy, sr + ts(3), 0, Math.PI * 2);
        ctx.fillStyle = '#0d2818';
        ctx.fill();
        // Sand
        const sg = ctx.createRadialGradient(sx - sr * 0.2, sy - sr * 0.2, 0, sx, sy, sr);
        sg.addColorStop(0, '#d4b87a');
        sg.addColorStop(0.6, '#c4a97d');
        sg.addColorStop(1, '#a08558');
        ctx.beginPath();
        ctx.arc(sx, sy, sr, 0, Math.PI * 2);
        ctx.fillStyle = sg;
        ctx.fill();
        // Texture
        for (let i = 0; i < 15; i++) {
          const dx = (Math.random() - 0.5) * sr * 1.6;
          const dy = (Math.random() - 0.5) * sr * 1.6;
          if (Math.hypot(dx, dy) < sr * 0.85) {
            ctx.fillStyle = `rgba(180,150,90,${0.15 + Math.random() * 0.2})`;
            ctx.fillRect(sx + dx, sy + dy, 1.5, 1.5);
          }
        }
      }
    };
    const drawWater = () => {
      const wx = tx(water.x),
        wy = ty(water.y),
        ww = ts(water.w),
        wh = ts(water.h);
      // Water body
      const wg = ctx.createLinearGradient(wx, wy, wx, wy + wh);
      wg.addColorStop(0, '#0a3050');
      wg.addColorStop(0.5, '#0d4068');
      wg.addColorStop(1, '#0a3050');
      ctx.beginPath();
      ctx.roundRect(wx, wy, ww, wh, ts(8));
      ctx.fillStyle = wg;
      ctx.fill();
      // Shimmer
      for (let i = 0; i < 5; i++) {
        const shimX = wx + 10 + i * ww / 5 + Math.sin(t * 2 + i) * 5;
        const shimY = wy + wh * 0.3 + Math.cos(t * 1.5 + i * 2) * 5;
        ctx.beginPath();
        ctx.ellipse(shimX, shimY, ts(8), ts(2), 0.2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(150,200,255,${0.08 + Math.sin(t * 3 + i) * 0.04})`;
        ctx.fill();
      }
      // Edge
      ctx.beginPath();
      ctx.roundRect(wx, wy, ww, wh, ts(8));
      ctx.strokeStyle = 'rgba(100,180,255,0.15)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      // Label
      ctx.fillStyle = 'rgba(100,180,255,0.3)';
      ctx.font = `${ts(8)}px Inter, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText('WATER', wx + ww / 2, wy + wh / 2 + ts(3));
    };
    const drawRocks = () => {
      for (const rock of rocks) {
        const rx = tx(rock.cx),
          ry = ty(rock.cy),
          rr = ts(rock.r);
        // Shadow
        ctx.beginPath();
        ctx.ellipse(rx + 2, ry + 3, rr * 1.1, rr * 0.7, 0, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0,0,0,0.3)';
        ctx.fill();
        // Rock body
        const rg = ctx.createRadialGradient(rx - rr * 0.3, ry - rr * 0.3, 0, rx, ry, rr);
        rg.addColorStop(0, '#8a8578');
        rg.addColorStop(0.6, '#6b6358');
        rg.addColorStop(1, '#4a4540');
        ctx.beginPath();
        ctx.arc(rx, ry, rr, 0, Math.PI * 2);
        ctx.fillStyle = rg;
        ctx.fill();
        // Highlight
        ctx.beginPath();
        ctx.arc(rx - rr * 0.25, ry - rr * 0.25, rr * 0.35, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.1)';
        ctx.fill();
      }
    };
    const drawBumpers = () => {
      for (const b of bumpers) {
        ctx.save();
        const bx = tx(b.x + b.w / 2),
          by = ty(b.y + b.h / 2);
        ctx.translate(bx, by);
        ctx.rotate(b.angle || 0);
        const bw = ts(b.w),
          bh = ts(b.h);
        // Shadow
        ctx.fillStyle = 'rgba(0,0,0,0.2)';
        ctx.fillRect(-bw / 2 + 2, -bh / 2 + 3, bw, bh);
        // Body
        const bg = ctx.createLinearGradient(0, -bh / 2, 0, bh / 2);
        bg.addColorStop(0, '#c4a97d');
        bg.addColorStop(0.5, '#B8965A');
        bg.addColorStop(1, '#a08050');
        ctx.fillStyle = bg;
        ctx.beginPath();
        ctx.roundRect(-bw / 2, -bh / 2, bw, bh, ts(4));
        ctx.fill();
        // Highlight
        ctx.fillStyle = 'rgba(255,255,255,0.15)';
        ctx.fillRect(-bw / 2 + 2, -bh / 2 + 1, bw - 4, bh * 0.3);
        ctx.restore();
      }
    };
    const drawWindmill = () => {
      const wx = tx(windmill.cx),
        wy = ty(windmill.cy);
      const al = ts(windmill.armLen),
        aw = ts(windmill.armWidth);
      // Center post shadow
      ctx.beginPath();
      ctx.arc(wx + 2, wy + 3, ts(10), 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0,0,0,0.3)';
      ctx.fill();
      // Rotating blades
      const angle = t * windmill.speed;
      for (let i = 0; i < 4; i++) {
        const a = angle + i * Math.PI / 2;
        ctx.save();
        ctx.translate(wx, wy);
        ctx.rotate(a);
        // Blade shadow
        ctx.fillStyle = 'rgba(0,0,0,0.15)';
        ctx.fillRect(-aw / 2 + 2, 3, aw, al);
        // Blade
        const bladeGrad = ctx.createLinearGradient(0, 0, 0, al);
        bladeGrad.addColorStop(0, '#d4b87a');
        bladeGrad.addColorStop(1, '#a08050');
        ctx.fillStyle = bladeGrad;
        ctx.beginPath();
        ctx.roundRect(-aw / 2, 0, aw, al, ts(3));
        ctx.fill();
        // Stripe
        ctx.fillStyle = 'rgba(255,255,255,0.12)';
        ctx.fillRect(-aw / 2 + 2, al * 0.2, aw - 4, al * 0.15);
        ctx.restore();
      }
      // Center hub
      const hubGrad = ctx.createRadialGradient(wx - 2, wy - 2, 0, wx, wy, ts(10));
      hubGrad.addColorStop(0, '#e0d0b0');
      hubGrad.addColorStop(1, '#8a7a5a');
      ctx.beginPath();
      ctx.arc(wx, wy, ts(10), 0, Math.PI * 2);
      ctx.fillStyle = hubGrad;
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.2)';
      ctx.lineWidth = 1;
      ctx.stroke();
      // Center dot
      ctx.beginPath();
      ctx.arc(wx, wy, ts(3), 0, Math.PI * 2);
      ctx.fillStyle = '#4a4030';
      ctx.fill();
    };
    const drawHole = () => {
      const hx = tx(HOLE.x),
        hy = ty(HOLE.y),
        hr = ts(HOLE.r);
      // Shadow
      ctx.beginPath();
      ctx.ellipse(hx, hy + ts(2), hr + ts(3), hr * 0.65 + ts(2), 0, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0,0,0,0.35)';
      ctx.fill();
      // Cup
      ctx.beginPath();
      ctx.arc(hx, hy, hr, 0, Math.PI * 2);
      ctx.fillStyle = '#040c07';
      ctx.fill();
      ctx.strokeStyle = 'rgba(220,220,220,0.2)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      // Inner
      ctx.beginPath();
      ctx.arc(hx, hy, hr - ts(3), 0, Math.PI * 2);
      ctx.fillStyle = '#020805';
      ctx.fill();
      // Flag
      const flagTop = hy - ts(FLAG_HEIGHT);
      ctx.beginPath();
      ctx.moveTo(hx + 1, hy);
      ctx.lineTo(hx + 1, flagTop);
      ctx.strokeStyle = '#d8d8d8';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(hx + 1, flagTop, ts(2.5), 0, Math.PI * 2);
      ctx.fillStyle = '#e0e0e0';
      ctx.fill();
      const wave = Math.sin(t * 2) * ts(3);
      ctx.beginPath();
      ctx.moveTo(hx + 1, flagTop + ts(2));
      ctx.quadraticCurveTo(hx + ts(14) + wave, flagTop + ts(8), hx + ts(24) + wave, flagTop + ts(6));
      ctx.lineTo(hx + 1, flagTop + ts(16));
      ctx.closePath();
      ctx.fillStyle = '#B8965A';
      ctx.fill();
      // Glow
      if (ballMoving) {
        const d = Math.hypot(ball.x - HOLE.x, ball.y - HOLE.y);
        if (d < 120) {
          const intensity = (120 - d) / 120;
          ctx.beginPath();
          ctx.arc(hx, hy, hr + ts(4) + Math.sin(t * 3) * ts(2), 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(184,150,90,${intensity * 0.5})`;
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      }
    };
    const drawTrail = () => {
      for (const p of trail) {
        const alpha = (1 - p.age / 35) * 0.3;
        if (alpha <= 0) continue;
        const sz = ts(ball.r) * (1 - p.age / 35) * 0.4;
        ctx.beginPath();
        ctx.arc(tx(p.x), ty(p.y), Math.max(sz, 1), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(184,150,90,${alpha})`;
        ctx.fill();
      }
    };
    const drawBall = () => {
      if (isSunk) return;
      const bx = tx(ball.x),
        by = ty(ball.y),
        br = ts(ball.r);
      // Shadow
      ctx.beginPath();
      ctx.ellipse(bx + 2, by + 4, br * 1.15, br * 0.55, 0, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0,0,0,0.3)';
      ctx.fill();
      // Glow near hole
      const dh = Math.hypot(ball.x - HOLE.x, ball.y - HOLE.y);
      if (dh < 80 && ballMoving) {
        ctx.beginPath();
        ctx.arc(bx, by, br + ts(6), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(184,150,90,${(80 - dh) / 80 * 0.25})`;
        ctx.fill();
      }
      // Ball
      const bg = ctx.createRadialGradient(bx - br * 0.3, by - br * 0.3, 0, bx, by, br);
      bg.addColorStop(0, '#FFFFFF');
      bg.addColorStop(0.4, '#F8F4EC');
      bg.addColorStop(1, '#D0C8B0');
      ctx.beginPath();
      ctx.arc(bx, by, br, 0, Math.PI * 2);
      ctx.fillStyle = bg;
      ctx.fill();
      ctx.strokeStyle = 'rgba(180,170,150,0.3)';
      ctx.lineWidth = 0.5;
      ctx.stroke();
      // Specular
      ctx.beginPath();
      ctx.arc(bx - br * 0.3, by - br * 0.3, br * 0.3, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.fill();
      // Idle pulse
      if (!ballMoving && !dragging) {
        const pulse = (Math.sin(t * 3) + 1) / 2;
        ctx.beginPath();
        ctx.arc(bx, by, br + ts(10) + pulse * ts(4), 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(184,150,90,${0.15 + pulse * 0.15})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
    };
    const drawAim = () => {
      if (!dragging) return;
      const dx = dragStart.x - dragCurrent.x;
      const dy = dragStart.y - dragCurrent.y;
      const dist = Math.hypot(dx, dy);
      if (dist < 5) return;
      const power = Math.min(dist / 120, 1);
      const angle = Math.atan2(dy, dx);
      const bx = tx(ball.x),
        by = ty(ball.y);
      // Aim dots
      const steps = Math.floor(power * 15) + 3;
      for (let i = 1; i <= steps; i++) {
        const frac = i / steps;
        const px = bx + Math.cos(angle) * ts(15 + i * 12);
        const py = by + Math.sin(angle) * ts(15 + i * 12);
        const alpha = (1 - frac) * 0.5;
        ctx.beginPath();
        ctx.arc(px, py, ts(2.5 - frac * 1.5), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(184,150,90,${alpha})`;
        ctx.fill();
      }
      // Rubber band
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.lineTo(tx(dragCurrent.x), ty(dragCurrent.y));
      ctx.strokeStyle = 'rgba(255,255,255,0.1)';
      ctx.lineWidth = 1;
      ctx.setLineDash([ts(3), ts(3)]);
      ctx.stroke();
      ctx.setLineDash([]);
      // Power arc
      const br = ts(ball.r);
      ctx.beginPath();
      ctx.arc(bx, by, br + ts(16), -Math.PI, -Math.PI + Math.PI * power, false);
      const pc = power < 0.4 ? '100,200,130' : power < 0.75 ? '184,150,90' : '220,100,80';
      ctx.strokeStyle = `rgba(${pc},0.8)`;
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.stroke();
      ctx.lineCap = 'butt';
      ctx.fillStyle = `rgba(${pc},0.9)`;
      ctx.font = `bold ${ts(9)}px Inter, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(`${Math.round(power * 100)}%`, bx, by - br - ts(22));
    };
    const drawParMarker = () => {
      // Physical tee marker near ball start area
      const mx = tx(LW / 2);
      const my = ty(LH - 18);
      // Small plaque/sign
      const pw = ts(52);
      const ph = ts(18);
      // Plaque shadow
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.3)';
      ctx.shadowBlur = 6;
      ctx.shadowOffsetY = 2;
      // Plaque body
      const plaqueGrad = ctx.createLinearGradient(mx - pw / 2, my - ph / 2, mx - pw / 2, my + ph / 2);
      plaqueGrad.addColorStop(0, '#2a2218');
      plaqueGrad.addColorStop(0.5, '#1e1a12');
      plaqueGrad.addColorStop(1, '#161210');
      ctx.beginPath();
      ctx.roundRect(mx - pw / 2, my - ph / 2, pw, ph, ts(3));
      ctx.fillStyle = plaqueGrad;
      ctx.fill();
      ctx.restore();
      // Gold border
      ctx.beginPath();
      ctx.roundRect(mx - pw / 2, my - ph / 2, pw, ph, ts(3));
      ctx.strokeStyle = 'rgba(184,150,90,0.4)';
      ctx.lineWidth = 1;
      ctx.stroke();
      // Inner border
      ctx.beginPath();
      ctx.roundRect(mx - pw / 2 + 2, my - ph / 2 + 2, pw - 4, ph - 4, ts(2));
      ctx.strokeStyle = 'rgba(184,150,90,0.15)';
      ctx.lineWidth = 0.5;
      ctx.stroke();
      // "PAR 3" text
      ctx.fillStyle = '#B8965A';
      ctx.font = `bold ${ts(8)}px "Playfair Display", serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('PAR 3', mx, my);
      ctx.textBaseline = 'alphabetic';
    };
    const drawHoleMarker = () => {
      // Small hole number marker near the hole
      const mx = tx(HOLE.x + 22);
      const my = ty(HOLE.y + 5);
      const mr = ts(8);
      // Circle marker
      ctx.beginPath();
      ctx.arc(mx, my, mr, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(15,10,5,0.6)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(184,150,90,0.35)';
      ctx.lineWidth = 1;
      ctx.stroke();
      // Hole number
      ctx.fillStyle = 'rgba(184,150,90,0.8)';
      ctx.font = `bold ${ts(7)}px "Playfair Display", serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('1', mx, my);
      ctx.textBaseline = 'alphabetic';
    };
    const drawUI = () => {
      // Putt counter only (par is now on the course)
      ctx.fillStyle = 'rgba(184,150,90,0.5)';
      ctx.font = '12px Inter, sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(`Putt ${Math.min(puttCount + 1, MAX_PUTTS)} / ${MAX_PUTTS}`, W - 20, 76);
    };
    // --- Physics ---
    const FRICTION = 0.988;
    const SAND_FRICTION = 0.94;
    // Circle-circle collision
    const circleCollide = (bx: number, by: number, bvx: number, bvy: number, br: number, cx: number, cy: number, cr: number, bounce: number) => {
      const dx = bx - cx,
        dy = by - cy;
      const dist = Math.hypot(dx, dy);
      const minDist = br + cr;
      if (dist < minDist && dist > 0) {
        const nx = dx / dist,
          ny = dy / dist;
        const overlap = minDist - dist;
        bx += nx * overlap;
        by += ny * overlap;
        const dot = bvx * nx + bvy * ny;
        bvx -= 2 * dot * nx * bounce;
        bvy -= 2 * dot * ny * bounce;
      }
      return {
        x: bx,
        y: by,
        vx: bvx,
        vy: bvy
      };
    };
    // Windmill blade collision
    const windmillCollide = () => {
      const angle = t * windmill.speed;
      for (let i = 0; i < 4; i++) {
        const a = angle + i * Math.PI / 2;
        // Check collision with blade as a line segment
        const tipX = windmill.cx + Math.sin(a) * windmill.armLen;
        const tipY = windmill.cy + Math.cos(a) * windmill.armLen;
        // Point-to-segment distance
        const segDx = tipX - windmill.cx;
        const segDy = tipY - windmill.cy;
        const segLen = Math.hypot(segDx, segDy);
        const segNx = segDx / segLen;
        const segNy = segDy / segLen;
        const toBallX = ball.x - windmill.cx;
        const toBallY = ball.y - windmill.cy;
        const proj = toBallX * segNx + toBallY * segNy;
        if (proj > 0 && proj < segLen) {
          const closestX = windmill.cx + segNx * proj;
          const closestY = windmill.cy + segNy * proj;
          const dist = Math.hypot(ball.x - closestX, ball.y - closestY);
          if (dist < ball.r + windmill.armWidth / 2) {
            // Push ball away from blade
            const pushX = ball.x - closestX;
            const pushY = ball.y - closestY;
            const pushDist = Math.hypot(pushX, pushY) || 1;
            ball.x += pushX / pushDist * 5;
            ball.y += pushY / pushDist * 5;
            // Add rotational velocity
            const perpX = -segNy * windmill.speed * 3;
            const perpY = segNx * windmill.speed * 3;
            ball.vx += perpX;
            ball.vy += perpY;
            ball.vx *= 0.7;
            ball.vy *= 0.7;
          }
        }
      }
    };
    const render = () => {
      t += 0.016;
      // Physics
      if (ballMoving && !isSunk) {
        ball.x += ball.vx;
        ball.y += ball.vy;
        // Check sand (slow down)
        let inSand = false;
        for (const s of sandTraps) {
          if (Math.hypot(ball.x - s.cx, ball.y - s.cy) < s.r) {
            inSand = true;
            break;
          }
        }
        const friction = inSand ? SAND_FRICTION : FRICTION;
        ball.vx *= friction;
        ball.vy *= friction;
        // Check water (reset)
        if (ball.x > water.x && ball.x < water.x + water.w && ball.y > water.y && ball.y < water.y + water.h) {
          // Splash particles
          for (let i = 0; i < 15; i++) {
            particles.push({
              x: ball.x,
              y: ball.y,
              vx: (Math.random() - 0.5) * 5,
              vy: -Math.random() * 6,
              life: 0.8,
              size: 2 + Math.random() * 3,
              color: '#6ab4e8'
            });
          }
          ball.x = LW / 2;
          ball.y = LH - 60;
          ball.vx = 0;
          ball.vy = 0;
          ballMoving = false;
        }
        // Wall bounces
        const wb = wallBounce(ball.x, ball.y, ball.vx, ball.vy, ball.r);
        ball.x = wb.x;
        ball.y = wb.y;
        ball.vx = wb.vx;
        ball.vy = wb.vy;
        // Rock collisions
        for (const rock of rocks) {
          const c = circleCollide(ball.x, ball.y, ball.vx, ball.vy, ball.r, rock.cx, rock.cy, rock.r, 0.75);
          ball.x = c.x;
          ball.y = c.y;
          ball.vx = c.vx;
          ball.vy = c.vy;
        }
        // Bumper collisions (simplified as circles at center)
        for (const b of bumpers) {
          const bcx = b.x + b.w / 2,
            bcy = b.y + b.h / 2;
          const c = circleCollide(ball.x, ball.y, ball.vx, ball.vy, ball.r, bcx, bcy, Math.max(b.w, b.h) / 2, 0.85);
          ball.x = c.x;
          ball.y = c.y;
          ball.vx = c.vx;
          ball.vy = c.vy;
        }
        // Windmill collision
        windmillCollide();
        // Trail
        const speed = Math.hypot(ball.vx, ball.vy);
        if (speed > 0.3) trail.push({
          x: ball.x,
          y: ball.y,
          age: 0
        });
        // Sink check
        const dh = Math.hypot(ball.x - HOLE.x, ball.y - HOLE.y);
        if (dh < HOLE.r + 2 && speed < 10) {
          isSunk = true;
          setShowSinkFlash(true);
          setTimeout(() => setShowSinkFlash(false), 600);
          for (let i = 0; i < 50; i++) {
            const a = Math.PI * 2 * i / 50;
            const sp = 2 + Math.random() * 7;
            particles.push({
              x: HOLE.x,
              y: HOLE.y,
              vx: Math.cos(a) * sp,
              vy: Math.sin(a) * sp,
              life: 1,
              size: 2 + Math.random() * 5,
              color: ['#B8965A', '#D4B87A', '#FAF8F3', '#FFD700'][Math.floor(Math.random() * 4)]
            });
          }
          setTimeout(() => triggerReveal(), 2500);
        }
        // Ball stopped
        if (speed < 0.12) {
          ball.vx = 0;
          ball.vy = 0;
          ballMoving = false;
        }
      }
      // Age trail
      for (let i = trail.length - 1; i >= 0; i--) {
        trail[i].age++;
        if (trail[i].age > 35) trail.splice(i, 1);
      }
      // Draw
      drawBackground();
      drawCourse();
      drawSandTraps();
      drawWater();
      drawTrail();
      drawRocks();
      drawBumpers();
      drawHole();
      drawHoleMarker();
      drawWindmill();
      drawParMarker();
      // Particles
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.1;
        p.life -= 0.013;
        if (p.life <= 0) {
          particles.splice(i, 1);
          continue;
        }
        ctx.beginPath();
        ctx.arc(tx(p.x), ty(p.y), ts(p.size) * p.life, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.life * p.life;
        ctx.fill();
        ctx.globalAlpha = 1;
      }
      drawBall();
      drawAim();
      drawUI();
      raf = requestAnimationFrame(render);
    };
    render();
    return () => {
      window.removeEventListener('resize', computeLayout);
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onUp);
      cancelAnimationFrame(raf);
    };
  }, [isRevealed, triggerReveal, resetTimeout]);
  return <section className="relative h-screen min-h-[800px] flex items-center justify-center overflow-hidden bg-[#081a0e]" data-id="element-386">
      <AnimatePresence data-id="element-387">
        {showSinkFlash && <motion.div initial={{
        opacity: 0.6
      }} animate={{
        opacity: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.6
      }} className="absolute inset-0 z-30 bg-brand-gold/20 pointer-events-none" data-id="element-388" />}
      </AnimatePresence>

      <AnimatePresence data-id="element-389">
        {!isRevealed && <motion.div initial={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 1.5
      }} className="absolute inset-0 z-10" data-id="element-390">
            <canvas ref={canvasRef} className="w-full h-full touch-none cursor-grab active:cursor-grabbing" data-id="element-391" />
            <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5,
          duration: 0.8
        }} className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center pointer-events-none" data-id="element-392">
              <span className="text-brand-gold/60 font-body text-xs sm:text-sm tracking-widest uppercase mb-2" data-id="element-393">
                Drag the ball · Sink it in the hole
              </span>
              <motion.div animate={{
            y: [0, 5, 0]
          }} transition={{
            repeat: Infinity,
            duration: 1.5,
            ease: 'easeInOut'
          }} className="w-5 h-5 rounded-full border border-brand-gold/25 flex items-center justify-center" data-id="element-394">
                <div className="w-1 h-1 rounded-full bg-brand-gold/40" data-id="element-395" />
              </motion.div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,#1d5233_0%,#143f27_30%,#10331f_60%,#081a0e_100%)] pointer-events-none" data-id="element-396" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(184,150,90,0.08)_0%,transparent_50%)] pointer-events-none" data-id="element-397" />
      <div className="contour-pattern absolute inset-0 opacity-20 pointer-events-none" data-id="element-398" />

      <div className="relative z-20 max-w-7xl mx-auto px-6 text-center pt-24 pb-32 pointer-events-none" data-id="element-399">
        <AnimatePresence data-id="element-400">
          {isRevealed && <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} transition={{
          duration: 1.2,
          ease: [0.21, 0.47, 0.32, 0.98]
        }} className="pointer-events-auto" data-id="element-401">
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-heading text-brand-cream-light leading-tight mb-6 max-w-5xl mx-auto glow-text" data-id="element-402">
                Premium putting experiences for world-class resorts and
                destinations.
              </h1>
              <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1,
            delay: 0.4
          }} className="text-xl md:text-2xl font-body text-brand-cream/80 max-w-3xl mx-auto mb-12 leading-relaxed" data-id="element-403">
                PuttLab designs and builds unforgettable golf amenities that
                transform resort spaces into social hubs guests never forget —
                from Cancún to the Caribbean and beyond.
              </motion.p>
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1,
            delay: 0.6
          }} className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6" data-id="element-404">
                <Button variant="primary" size="lg" href="/concepts" data-id="element-405">
                  Explore Concepts
                </Button>
                <Button variant="secondary" size="lg" href="/contact" className="!text-brand-cream-light !border-brand-cream-light/70 hover:!bg-brand-cream-light hover:!text-brand-green-deep" data-id="element-406">
                  Book a Design Call
                </Button>
              </motion.div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}